/**
 * 集中放 /api/organizations/:orgCode/* URL builder fn，避免 panel 里散落字符串。
 * Phase C.2 super-admin scope 路由组。
 */

const base = (orgCode: string): string =>
  `/api/organizations/${encodeURIComponent(orgCode)}`;

export const organizationCapabilitiesPath = (orgCode: string): string =>
  `${base(orgCode)}/capabilities`;

export const organizationCapabilityPath = (orgCode: string, catalogItemId: string): string =>
  `${organizationCapabilitiesPath(orgCode)}/${encodeURIComponent(catalogItemId)}`;

export const usersPath = (orgCode: string, sub: string = ""): string =>
  `${base(orgCode)}/users${sub}`;

export const userByIdPath = (orgCode: string, userId: string, sub: string = ""): string =>
  `${base(orgCode)}/users/${userId}${sub}`;

export const batchPatchUsersPath = (orgCode: string): string =>
  `${base(orgCode)}/users/batch-patch`;

/** 员工设备版本（2026-10-16）：员工列表「DOSIA 版本」列的数据源（只读透传 dosia-enterprise）。 */
export const clientDevicesPath = (orgCode: string): string =>
  `${base(orgCode)}/client-devices`;

export const pinyinMigrationPath = (
  orgCode: string,
  action: "dry-run" | "apply",
): string => `${base(orgCode)}/users/pinyin-migration/${action}`;

export const rolesPath = (orgCode: string, sub: string = ""): string =>
  `${base(orgCode)}/roles${sub}`;

export const roleByIdPath = (orgCode: string, roleId: string): string =>
  `${base(orgCode)}/roles/${roleId}`;

export const leadershipPath = (orgCode: string, sub: string = ""): string =>
  `${base(orgCode)}/leadership${sub}`;

export const leadershipByRolePath = (orgCode: string, roleCode: string): string =>
  `${base(orgCode)}/leadership/${encodeURIComponent(roleCode)}`;

export const auditPath = (orgCode: string): string => `${base(orgCode)}/audit`;

/** N-76 R6.1 — 项目管理员名单：GET 取当前 { users }，PUT 整体替换。 */
export const projectAdminsPath = (orgCode: string): string =>
  `${base(orgCode)}/project-admins`;

export const mcpReviewPendingPath = (orgCode: string): string =>
  `${base(orgCode)}/mcp-review/pending`;

export const mcpReviewApprovePath = (orgCode: string, id: string): string =>
  `${base(orgCode)}/mcp-review/${encodeURIComponent(id)}/approve`;

export const mcpReviewRejectPath = (orgCode: string, id: string): string =>
  `${base(orgCode)}/mcp-review/${encodeURIComponent(id)}/reject`;

// ---- 技能/Agent 审批中心（dosia-enterprise 服务间通道透传，2026-07） ----

export const reviewsPendingPath = (orgCode: string): string =>
  `${base(orgCode)}/reviews/pending`;

export const reviewsPromotePath = (orgCode: string): string =>
  `${base(orgCode)}/reviews/promote`;

export const reviewsRejectPath = (orgCode: string): string =>
  `${base(orgCode)}/reviews/reject`;

// PR-C：已发布 org agent 列表 + 字段级直发编辑重发布。
export const reviewsPublishedPath = (orgCode: string): string =>
  `${base(orgCode)}/reviews/published`;

export const reviewsPublishDirectPath = (orgCode: string): string =>
  `${base(orgCode)}/reviews/publish-direct`;

// N-83 P3-01：已发布 Agent「发布到飞书」（apps/admin-portal/server/routes/orgs-agent-feishu.ts，
// 挂在 /reviews/published/:slug/feishu 下，与 /published 同一条中间件链）。
export const reviewsPublishedFeishuPath = (orgCode: string, slug: string): string =>
  `${reviewsPublishedPath(orgCode)}/${encodeURIComponent(slug)}/feishu`;

export const reviewsPublishedFeishuDisablePath = (orgCode: string, slug: string): string =>
  `${reviewsPublishedFeishuPath(orgCode, slug)}/disable`;

export const reviewsPublishedFeishuEnablePath = (orgCode: string, slug: string): string =>
  `${reviewsPublishedFeishuPath(orgCode, slug)}/enable`;

// ---- 企业自动化任务中心（dosia-enterprise 服务间通道透传，2026-07） ----

export const tasksPath = (orgCode: string): string => `${base(orgCode)}/tasks`;

export const taskByIdPath = (orgCode: string, taskId: string): string =>
  `${base(orgCode)}/tasks/${encodeURIComponent(taskId)}`;

export const taskRunNowPath = (orgCode: string, taskId: string): string =>
  `${base(orgCode)}/tasks/${encodeURIComponent(taskId)}/run-now`;

export const taskExecutionsPath = (
  orgCode: string,
  query: { taskId?: string; limit?: number; cursor?: string } = {},
): string => {
  const qs = new URLSearchParams();
  if (query.taskId) qs.set("taskId", query.taskId);
  if (query.limit != null) qs.set("limit", String(query.limit));
  if (query.cursor) qs.set("cursor", query.cursor);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return `${base(orgCode)}/tasks/executions${suffix}`;
};

export const taskExecutionByIdPath = (
  orgCode: string,
  executionId: string,
): string => `${base(orgCode)}/tasks/executions/${encodeURIComponent(executionId)}`;

// ---- 集成与凭据中心（dosia-enterprise 服务间通道透传，2026-07） ----

export const integrationsPath = (orgCode: string): string =>
  `${base(orgCode)}/integrations`;

// ---- 企业模型管理中心（dosia-enterprise 服务间通道透传，2026-07） ----

export const modelsRawPath = (orgCode: string): string =>
  `${base(orgCode)}/models/raw`;

export const modelsOverridePath = (orgCode: string): string =>
  `${base(orgCode)}/models/override`;

export const modelsBroadcastPath = (orgCode: string): string =>
  `${base(orgCode)}/models/broadcast`;

export const modelsTakeoverPath = (orgCode: string): string =>
  `${base(orgCode)}/models/takeover`;

export const modelsMembersPath = (orgCode: string): string =>
  `${base(orgCode)}/models/members`;

/** 席位总览 roster（全员一行一人的席位裁决 + 订阅健康度摘要）：GET 只读，服务端实时计算。 */
export const modelsSeatsPath = (orgCode: string): string =>
  `${base(orgCode)}/models/seats`;

/** 单员工席位详情（订阅健康度窗口 + claude 模型逐个来源裁决）：GET 只读，服务端实时计算。 */
export const modelSeatDetailPath = (orgCode: string, employeeId: string): string =>
  `${base(orgCode)}/models/seats/${encodeURIComponent(employeeId)}`;

/** 组织级记忆开关默认（P2）：GET 读当前 / PUT 切换（true=开→$unset / false=关）。存本库。 */
export const modelsMemoryEnabledPath = (orgCode: string): string =>
  `${base(orgCode)}/models/memory-enabled`;

/** 组织级云端任务日预算：GET 读/PUT 存正数或清空（null/0/负数 = 无限）。存本库。 */
export const modelsCloudTaskBudgetPath = (orgCode: string): string =>
  `${base(orgCode)}/models/cloud-task-budget`;

/** 组织级盯盘 AI 每员工每日次数上限（N-57 期 4 D4-03）：GET 读 / PUT 存非负整数或 null（清空 = 默认 20）。存本库。 */
export const modelsMonitorAiDailyLimitPath = (orgCode: string): string =>
  `${base(orgCode)}/models/monitor-ai-daily-limit`;

/** 组织级模型圈定（三层漏斗中层）：GET 读当前 ModelSelection / PUT 整体替换主脑·工具两线。
 *  独立路由（不在 /models/ 下），缺省字段 = 该线继承上层全量。存本库。 */
export const modelSelectionPath = (orgCode: string): string =>
  `${base(orgCode)}/model-selection`;

/** 组织级能力桶值班表（text_generate / agent_main）：GET 读当前 / PUT 整体替换。 */
export const orgModelBucketCurationPath = (orgCode: string): string =>
  `${base(orgCode)}/model-bucket-curation`;

/** 网关现行模型 id 集合（N-88，组织管理员可读；只回 base id + 是否 live + 取数时间，判「已下线」用）。 */
export const orgLiveModelIdsPath = (orgCode: string): string =>
  `${base(orgCode)}/live-model-ids`;

/** 平台模型治理只读快照（无鉴权；含 brainPriority）。 */
export const publicModelGovernancePath = (): string =>
  "/api/public/model-governance";

// ---- 企业记忆治理 · 全员蒸馏（dosia-enterprise 服务间通道透传，P5-B） ----

/** 发起全员蒸馏（POST，后台 job，立即返回 { started, runId }）。 */
export const memoryDistillAllPath = (orgCode: string): string =>
  `${base(orgCode)}/memory/distill-all`;

/** 轮询蒸馏运行进度（GET，{ processed, skipped, failed, total, ... } | null）。 */
export const memoryDistillStatusPath = (orgCode: string): string =>
  `${base(orgCode)}/memory/distill-all/status`;

export const feishuPath = (orgCode: string, sub: string = ""): string =>
  `${base(orgCode)}/feishu${sub}`;

export const feishuDepartmentsPath = (orgCode: string): string =>
  feishuPath(orgCode, "/departments");

export const feishuSyncPath = (orgCode: string): string =>
  feishuPath(orgCode, "/sync");

export const feishuDeptMembersPath = (
  orgCode: string,
  openDepartmentId: string,
): string =>
  feishuPath(
    orgCode,
    `/departments/${encodeURIComponent(openDepartmentId)}/members`,
  );

// ---- 组织搭建 onboarding 引导进度 ----

export const onboardingPath = (orgCode: string): string =>
  `${base(orgCode)}/onboarding`;

export const bootstrapPath = (orgCode: string): string =>
  `${base(orgCode)}/bootstrap`;

// ---- Phase F · 角色推荐 + LLM 配置 ----

export const roleProvisioningPath = (
  orgCode: string,
  sub: string = "",
): string => `${base(orgCode)}/role-provisioning${sub}`;

export const roleProvisioningByIdPath = (
  orgCode: string,
  taskId: string,
  sub: string = "",
): string => `${base(orgCode)}/role-provisioning/${taskId}${sub}`;

export const roleProvisioningStartPath = (orgCode: string): string =>
  roleProvisioningPath(orgCode, "/start");

export const llmConfigPath = (orgCode: string): string =>
  `${base(orgCode)}/llm-config`;

export const llmConfigTestPath = (orgCode: string): string =>
  `${base(orgCode)}/llm-config/test`;

/** 云端执行应用（feishu-cloud tenant 身份）登记：GET 脱敏读 / PUT 加密写。本库自有存储。 */
export const cloudFeishuAppPath = (orgCode: string): string =>
  `${base(orgCode)}/cloud-feishu-app`;

// ---- 企业入驻（平台级审批队列，super-only，非 org-scoped） ----

export const onboardingApplicationsPath = (status?: string): string =>
  `/api/onboarding/applications${status ? `?status=${encodeURIComponent(status)}` : ""}`;

export const onboardingApplicationApprovePath = (id: string): string =>
  `/api/onboarding/applications/${encodeURIComponent(id)}/approve`;

export const onboardingApplicationRejectPath = (id: string): string =>
  `/api/onboarding/applications/${encodeURIComponent(id)}/reject`;

export const onboardingInvitesPath = (): string => "/api/onboarding/invites";

export const onboardingInviteRevokePath = (id: string): string =>
  `/api/onboarding/invites/${encodeURIComponent(id)}/revoke`;

export const onboardingInviteRetryPath = (id: string): string =>
  `/api/onboarding/invites/${encodeURIComponent(id)}/retry-provisioning`;

export const onboardingInviteMarkChargedPath = (id: string): string =>
  `/api/onboarding/invites/${encodeURIComponent(id)}/mark-charged`;

export const onboardingGatewayCredentialPath = (): string =>
  "/api/onboarding/gateway-credential";

/** L1.5 平台策展-主脑清单（super-only 平台面，非 org-scoped —— 全平台单份策展）。
 *  GET 读当前 / PUT 整体替换白名单。 */
export const platformBrainCurationPath = (): string =>
  "/api/platform/brain-curation";

/** 全平台默认主脑优先级列表（super-only 平台面，非 org-scoped）。 */
export const platformBrainPriorityPath = (): string =>
  "/api/platform/brain-priority";

/** L1.5 平台策展-工具模型清单（super-only 平台面，非 org-scoped —— 全平台单份策展）。
 *  GET 读当前 / PUT 整体替换（视觉/生图/视频清单，整字段替换，缺省 = 回落代码 snapshot 常量）。 */
export const platformToolCurationPath = (): string =>
  "/api/platform/tool-curation";

/** pi 策展平台级（super-only 平台面，非 org-scoped —— 全平台单份「pi 母语引擎」白名单）。
 *  GET 读当前 / PUT 整体替换（{ models }，去 [1m] suffix / 去空去重，空 = 未策展）。 */
export const platformPiCurationPath = (): string =>
  "/api/platform/pi-curation";

/** 平台级档位表（super-only 平台面，非 org-scoped —— 全平台单份档位映射）。
 *  GET 读当前 / PUT 整体替换（{ tiers }，空条目剔除，空表 = 无档位表）。 */
export const platformTierTablePath = (): string =>
  "/api/platform/tier-table";

/** 平台策展候选源（super-only 平台面，非 org-scoped）—— 模型清单枢纽页与三个策展页共用。
 *  GET { entries: ModelCatalogEntry[], source: "live" | "unavailable" }（unavailable = 网关
 *  未配置 / 不可达 / 未发布，entries 恒空；N-95 起不回落随包快照，见 usePlatformModelCatalog）。 */
export const platformModelCatalogPath = (): string =>
  "/api/platform/model-catalog";

/** 网关下发的档位默认（`tier_defaults`）只读视图（super-only 平台面，非 org-scoped）。
 *  GET { groups: GatewayTierDefaultsGroup[], source: "live" | "unavailable" }。
 *  **只读**：改档位要去网关侧改价格/账号，不是在 DOSIA 后台编辑（故无 PUT）。 */
export const platformGatewayTierDefaultsPath = (): string =>
  "/api/platform/gateway-tier-defaults";

/** 超管一键测试上游模型可用性（super-only 平台面，非 org-scoped）。POST { scope?: "stale" | "all" }
 *  → { tested, available, unavailable, testedAt }，打网关批量可用性测试端点。 */
export const platformUpstreamAvailabilityTestPath = (): string =>
  "/api/platform/upstream-availability-test";

/** 超管「模型诊断」（N-156 只读接口，super-only 平台面，非 org-scoped）。GET → ModelAvailabilityDiagnosis
 *  （@dosia/shared `parseModelAvailabilityDiagnosis` 做边界 guard）；员工必须带组织（服务端 400 org-required-for-employee）。 */
export const platformModelDiagnosisPath = (query: { model: string; org?: string; employee?: string }): string => {
  const params = new URLSearchParams({ model: query.model });
  if (query.org) params.set("org", query.org);
  if (query.employee) params.set("employee", query.employee);
  return `/api/platform/model-diagnosis?${params.toString()}`;
};

// ---- Capability Center 平台只读目录（super-only，非 org-scoped；P0C） ----

/** GET 平台能力总库列表（strict CapabilityCatalogListResponseV1）。 */
export const platformCapabilitiesPath = (): string => "/api/platform/capabilities";

/** GET 单条能力详情（strict CapabilityCatalogDetailResponseV1）。 */
export const platformCapabilityPath = (catalogItemId: string): string =>
  `/api/platform/capabilities/${encodeURIComponent(catalogItemId)}`;

// ---- Skill Hub 平台 Catalog（super-only，非 org-scoped；PR-3） ----

const skillHubBase = (): string => "/api/platform/skill-hub";

/** GET 列表 / POST 新建 */
export const platformSkillHubItemsPath = (): string => `${skillHubBase()}/items`;

export const platformSkillHubItemPath = (itemId: string): string =>
  `${skillHubBase()}/items/${encodeURIComponent(itemId)}`;

export const platformSkillHubArchivePath = (itemId: string): string =>
  `${platformSkillHubItemPath(itemId)}/archive`;

export const platformSkillHubValidatePath = (itemId: string): string =>
  `${platformSkillHubItemPath(itemId)}/validate`;

export const platformSkillHubPublishPath = (itemId: string): string =>
  `${platformSkillHubItemPath(itemId)}/publish`;

export const platformSkillHubUnpublishPath = (itemId: string): string =>
  `${platformSkillHubItemPath(itemId)}/unpublish`;

export const platformSkillHubRollbackPath = (itemId: string): string =>
  `${platformSkillHubItemPath(itemId)}/rollback`;
